import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Snowflake, Bell, ArrowLeft, AlertTriangle, TrendingUp, Clock,
  DollarSign, Zap, Plus, Trash2, Edit, CheckCircle, XCircle,
  Mail, MessageSquare, Settings, Activity, RefreshCw, Database,
  Users, Server, Filter, BellRing, BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const SnowflakeAlerts = () => {
  const { shouldShowData } = useDemoData();
  // Iter84al — ping real backend on mount; local hardcoded list still
  // drives the rich UI rendering.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/snowflake-alerts/recent`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  }, []);
  const [showNewAlert, setShowNewAlert] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      name: 'Daily Credit Spike',
      type: 'credit_spike',
      condition: 'Credits exceed 150% of daily average',
      threshold: 150,
      warehouse: 'All Warehouses',
      enabled: true,
      channels: ['email', 'slack'],
      lastTriggered: '2026-03-05 03:45:00',
      triggerCount: 3
    },
    {
      id: 2,
      name: 'Expensive Query Alert',
      type: 'query_cost',
      condition: 'Single query cost exceeds $100',
      threshold: 100,
      warehouse: 'ANALYTICS_WH',
      enabled: true,
      channels: ['email'],
      lastTriggered: '2026-03-05 04:30:00',
      triggerCount: 7
    },
    {
      id: 3,
      name: 'Warehouse Idle Warning',
      type: 'warehouse_idle',
      condition: 'Warehouse running with 0% utilization for 30+ minutes',
      threshold: 30,
      warehouse: 'All Warehouses',
      enabled: true,
      channels: ['slack'],
      lastTriggered: '2026-03-04 22:15:00',
      triggerCount: 2
    },
    {
      id: 4,
      name: 'Weekly Budget Alert',
      type: 'budget',
      condition: 'Weekly spend exceeds $20,000',
      threshold: 20000,
      warehouse: 'All Warehouses',
      enabled: false,
      channels: ['email'],
      lastTriggered: null,
      triggerCount: 0
    },
    {
      id: 5,
      name: 'User Spending Limit',
      type: 'user_spend',
      condition: 'Single user exceeds $500/day',
      threshold: 500,
      warehouse: 'All Warehouses',
      enabled: true,
      channels: ['email', 'slack'],
      lastTriggered: '2026-03-05 10:00:00',
      triggerCount: 1
    },
  ]);

  const [recentAlertEvents] = useState([
    {
      id: 1,
      alertName: 'Daily Credit Spike',
      message: 'Credits reached 234% of daily average on ANALYTICS_WH',
      severity: 'high',
      timestamp: '2026-03-05 03:45:00',
      details: {
        warehouse: 'ANALYTICS_WH',
        actualValue: 2341,
        expectedValue: 1000,
        causedBy: 'john.smith@company.com',
        query: 'Q-7834'
      }
    },
    {
      id: 2,
      alertName: 'Expensive Query Alert',
      message: 'Query Q-7834 cost $586.25',
      severity: 'high',
      timestamp: '2026-03-05 04:30:35',
      details: {
        queryId: 'Q-7834',
        user: 'john.smith@company.com',
        warehouse: 'ANALYTICS_WH',
        cost: 586.25
      }
    },
    {
      id: 3,
      alertName: 'User Spending Limit',
      message: 'data_team@company.com spent $786 today',
      severity: 'medium',
      timestamp: '2026-03-05 10:00:00',
      details: {
        user: 'data_team@company.com',
        dailySpend: 786,
        threshold: 500
      }
    },
    {
      id: 4,
      alertName: 'Warehouse Idle Warning',
      message: 'DEV_WH running idle for 45 minutes',
      severity: 'low',
      timestamp: '2026-03-04 22:15:00',
      details: {
        warehouse: 'DEV_WH',
        idleTime: '45 minutes',
        wastedCredits: 12.5
      }
    },
  ]);

  const [newAlert, setNewAlert] = useState({
    name: '',
    type: 'credit_spike',
    threshold: '',
    warehouse: 'all',
    channels: ['email']
  });

  const toggleAlert = (id) => {
    setAlerts(alerts.map(alert => 
      alert.id === id ? { ...alert, enabled: !alert.enabled } : alert
    ));
    const alert = alerts.find(a => a.id === id);
    toast.success(`Alert "${alert.name}" ${alert.enabled ? 'disabled' : 'enabled'}`);
  };

  const deleteAlert = (id) => {
    const alert = alerts.find(a => a.id === id);
    setAlerts(alerts.filter(a => a.id !== id));
    toast.success(`Alert "${alert.name}" deleted`);
  };

  const createAlert = () => {
    const newId = Math.max(...alerts.map(a => a.id)) + 1;
    const alertConfig = {
      credit_spike: { condition: `Credits exceed ${newAlert.threshold}% of daily average` },
      query_cost: { condition: `Single query cost exceeds $${newAlert.threshold}` },
      warehouse_idle: { condition: `Warehouse idle for ${newAlert.threshold}+ minutes` },
      budget: { condition: `Spend exceeds $${newAlert.threshold}` },
      user_spend: { condition: `User exceeds $${newAlert.threshold}/day` },
    };
    
    setAlerts([...alerts, {
      id: newId,
      name: newAlert.name,
      type: newAlert.type,
      condition: alertConfig[newAlert.type].condition,
      threshold: parseFloat(newAlert.threshold),
      warehouse: newAlert.warehouse === 'all' ? 'All Warehouses' : newAlert.warehouse,
      enabled: true,
      channels: newAlert.channels,
      lastTriggered: null,
      triggerCount: 0
    }]);
    
    setShowNewAlert(false);
    setNewAlert({ name: '', type: 'credit_spike', threshold: '', warehouse: 'all', channels: ['email'] });
    toast.success(`Alert "${newAlert.name}" created`);
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'credit_spike': return <TrendingUp className="w-5 h-5 text-amber-500" />;
      case 'query_cost': return <DollarSign className="w-5 h-5 text-red-500" />;
      case 'warehouse_idle': return <Server className="w-5 h-5 text-emerald-500" />;
      case 'budget': return <Zap className="w-5 h-5 text-emerald-500" />;
      case 'user_spend': return <Users className="w-5 h-5 text-emerald-500" />;
      default: return <Bell className="w-5 h-5 text-slate-400" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const activeAlerts = alerts.filter(a => a.enabled).length;
  const totalTriggers = alerts.reduce((sum, a) => sum + a.triggerCount, 0);

  if (!shouldShowData) return <EmptyDataState title="Snowflake Alerts" description="Connect your Snowflake account to set up cost alerts and anomaly detection." />;
  return (
    <div className="space-y-6" data-testid="snowflake-alerts">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Bell className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Snowflake Alerts</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Real-time credit spike detection and notifications</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            setRefreshing(true);
            setTimeout(() => {
              setRefreshing(false);
              toast.success('Alerts refreshed');
            }, 1500);
          }} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowNewAlert(true)} className="bg-rose-500 hover:bg-rose-600">
            <Plus className="w-4 h-4 mr-2" />
            Create Alert
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Active Alerts</p>
                <p className="text-2xl font-bold text-emerald-600">{activeAlerts}</p>
              </div>
              <BellRing className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Disabled</p>
                <p className="text-2xl font-bold text-slate-400">{alerts.length - activeAlerts}</p>
              </div>
              <BellOff className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Triggers (7d)</p>
                <p className="text-2xl font-bold text-amber-600">{totalTriggers}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Avg Response</p>
                <p className="text-2xl font-bold text-emerald-600">&lt;2min</p>
              </div>
              <Clock className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Status Banner */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
          <div>
            <p className="font-medium text-slate-900 dark:text-white">Real-time Monitoring Active</p>
            <p className="text-sm text-slate-400 dark:text-slate-400">
              Credit burn rate: <span className="font-semibold text-emerald-600">$47/hour</span> (normal range)
            </p>
          </div>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          All systems normal
        </Badge>
      </div>

      {/* Alert Rules */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-400" />
            Alert Rules
          </CardTitle>
          <CardDescription>Configure when you want to be notified</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div 
                key={alert.id} 
                className={`p-4 rounded-xl border transition-all ${
                  alert.enabled 
                    ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' 
                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700/50 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      alert.enabled ? 'bg-slate-100 dark:bg-slate-700' : 'bg-slate-200 dark:bg-slate-600'
                    }`}>
                      {getAlertTypeIcon(alert.type)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{alert.name}</p>
                      <p className="text-sm text-slate-400">{alert.condition}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{alert.warehouse}</Badge>
                        {alert.channels.includes('email') && (
                          <Badge variant="outline" className="text-xs">
                            <Mail className="w-3 h-3 mr-1" />Email
                          </Badge>
                        )}
                        {alert.channels.includes('slack') && (
                          <Badge variant="outline" className="text-xs">
                            <MessageSquare className="w-3 h-3 mr-1" />Slack
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-slate-400">Last triggered</p>
                      <p className="font-medium text-slate-700 dark:text-slate-300">
                        {alert.lastTriggered || 'Never'}
                      </p>
                      <p className="text-xs text-slate-400">{alert.triggerCount} times (7d)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={alert.enabled}
                        onCheckedChange={() => toggleAlert(alert.id)}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-slate-400 hover:text-red-500"
                        onClick={() => deleteAlert(alert.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Alert Events */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-500" />
            Recent Alert Events
          </CardTitle>
          <CardDescription>Latest notifications triggered by your alerts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentAlertEvents.map((event) => (
              <div key={event.id} className={`p-4 rounded-xl border ${getSeverityColor(event.severity)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={getSeverityColor(event.severity)}>
                        {event.severity.toUpperCase()}
                      </Badge>
                      <span className="font-semibold text-slate-900">{event.alertName}</span>
                    </div>
                    <p className="text-slate-700 mb-2">{event.message}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {Object.entries(event.details).map(([key, value]) => (
                        <span key={key} className="px-2 py-1 rounded bg-white/50 text-slate-400">
                          {key}: <span className="font-medium">{value}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-400 whitespace-nowrap">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {event.timestamp}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Alert Dialog */}
      <Dialog open={showNewAlert} onOpenChange={setShowNewAlert}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-rose-500" />
              Create New Alert
            </DialogTitle>
            <DialogDescription>
              Set up a new alert rule for your Snowflake account
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Alert Name</Label>
              <Input
                placeholder="e.g., High Cost Query Alert"
                value={newAlert.name}
                onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Alert Type</Label>
              <Select value={newAlert.type} onValueChange={(v) => setNewAlert({ ...newAlert, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit_spike">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-500" />
                      Credit Spike (% of average)
                    </div>
                  </SelectItem>
                  <SelectItem value="query_cost">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-red-500" />
                      Query Cost ($)
                    </div>
                  </SelectItem>
                  <SelectItem value="warehouse_idle">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-emerald-500" />
                      Warehouse Idle (minutes)
                    </div>
                  </SelectItem>
                  <SelectItem value="budget">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-500" />
                      Budget Threshold ($)
                    </div>
                  </SelectItem>
                  <SelectItem value="user_spend">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-500" />
                      User Daily Spend ($)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Threshold</Label>
              <Input
                type="number"
                placeholder={newAlert.type === 'credit_spike' ? 'e.g., 150 (%)' : 'e.g., 100'}
                value={newAlert.threshold}
                onChange={(e) => setNewAlert({ ...newAlert, threshold: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Warehouse</Label>
              <Select value={newAlert.warehouse} onValueChange={(v) => setNewAlert({ ...newAlert, warehouse: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouses</SelectItem>
                  <SelectItem value="ANALYTICS_WH">ANALYTICS_WH</SelectItem>
                  <SelectItem value="ETL_WH">ETL_WH</SelectItem>
                  <SelectItem value="REPORTING_WH">REPORTING_WH</SelectItem>
                  <SelectItem value="DATA_SCIENCE_WH">DATA_SCIENCE_WH</SelectItem>
                  <SelectItem value="DEV_WH">DEV_WH</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notification Channels</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newAlert.channels.includes('email')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewAlert({ ...newAlert, channels: [...newAlert.channels, 'email'] });
                      } else {
                        setNewAlert({ ...newAlert, channels: newAlert.channels.filter(c => c !== 'email') });
                      }
                    }}
                    className="rounded border-slate-300"
                  />
                  <Mail className="w-4 h-4 text-slate-400" />
                  Email
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newAlert.channels.includes('slack')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewAlert({ ...newAlert, channels: [...newAlert.channels, 'slack'] });
                      } else {
                        setNewAlert({ ...newAlert, channels: newAlert.channels.filter(c => c !== 'slack') });
                      }
                    }}
                    className="rounded border-slate-300"
                  />
                  <MessageSquare className="w-4 h-4 text-slate-400" />
                  Slack
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAlert(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createAlert}
              disabled={!newAlert.name || !newAlert.threshold}
              className="bg-rose-500 hover:bg-rose-600"
            >
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SnowflakeAlerts;
